import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Folder, 
  Star, 
  Zap, 
  Heart, 
  Bookmark, 
  Code, 
  Globe,
  Briefcase
} from 'lucide-react';
import { clsx } from 'clsx';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface AddGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (group: any) => void;
  title?: string;
}

const ICONS = [Folder, Star, Zap, Heart, Bookmark, Code, Globe, Briefcase];
const COLORS = [
  'bg-blue-500', 
  'bg-emerald-500', 
  'bg-rose-500', 
  'bg-amber-500', 
  'bg-purple-500', 
  'bg-pink-500',
  'bg-slate-500'
];

export const AddGroupModal: React.FC<AddGroupModalProps> = ({ isOpen, onClose, onAdd, title = "Create New Group" }) => {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);

  useDialogA11y(dialogRootRef, { isOpen, onClose });

  if (!isOpen) return null;

  const IconComp = ICONS[selectedIcon];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Container */}
      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-group-modal-title"
        tabIndex={-1}
        className="relative w-full max-w-md bg-card border border-border modal-surface shadow-modal overflow-hidden animate-in side-in-from-bottom-4 zoom-in-95 duration-300 focus:outline-none"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-xl transition-all"
        >
          <X size={18} aria-hidden />
        </button>

        <div className="p-10 space-y-10">
          <div className="flex flex-col items-center text-center gap-6">
            <div className={clsx(
              "w-20 h-20 rounded-xl flex items-center justify-center text-primary-foreground transition-all duration-500",
              COLORS[selectedColor]
            )}>
              <IconComp size={36} strokeWidth={2.5} />
            </div>
            <div>
              <h2 id="add-group-modal-title" className="text-2xl font-black text-foreground tracking-tight">{title}</h2>
              <p className="text-sm text-muted-foreground mt-1 font-medium">{t('shared.modals.addGroup.description')}</p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Input */}
            <div className="space-y-3">
               <label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1">{t('shared.modals.addGroup.nameLabel')}</label>
               <input 
                 autoFocus
                 type="text" 
                 placeholder={t('shared.modals.addGroup.namePlaceholder')}
                 value={name}
                 onChange={(e) => setName(e.target.value)}
                 className="w-full bg-secondary border border-border rounded-2xl py-4 px-6 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold"
               />
            </div>

            {/* Colors */}
            <div className="space-y-3">
               <label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1">{t('shared.modals.addGroup.colorLabel')}</label>
               <div className="flex flex-wrap gap-3">
                  {COLORS.map((c, i) => (
                    <button 
                      key={i}
                      onClick={() => setSelectedColor(i)}
                      className={clsx(
                        "w-8 h-8 rounded-full transition-all border-4",
                        c,
                        selectedColor === i ? "border-foreground/20 scale-125" : "border-transparent scale-100 opacity-60 hover:opacity-100"
                      )}
                    />
                  ))}
               </div>
            </div>

            {/* Icons */}
            <div className="space-y-3">
               <label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1">{t('shared.modals.addGroup.iconLabel')}</label>
               <div className="grid grid-cols-4 gap-3">
                  {ICONS.map((Icon, i) => (
                    <button 
                      key={i}
                      onClick={() => setSelectedIcon(i)}
                      className={clsx(
                        "p-4 rounded-2xl border transition-all flex items-center justify-center",
                        selectedIcon === i 
                          ? "bg-secondary border-border text-foreground" 
                          : "bg-secondary/50 border-border-muted text-muted-foreground hover:border-border hover:text-foreground"
                      )}
                    >
                      <Icon size={20} />
                    </button>
                  ))}
               </div>
            </div>
          </div>

          <button 
            disabled={!name}
            onClick={() => onAdd({ name, icon: selectedIcon, color: selectedColor })}
            className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground py-4 rounded-2xl font-black active:scale-95 transition-all text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('shared.modals.addGroup.create')}
          </button>
        </div>
      </div>
    </div>
  );
};
